import { Alert, Button, Modal } from "antd";
import { useTranslation } from "react-i18next";

import { formatConflictMessage } from "./conflicts";
import { ExportConflict } from "./types";

interface ConflictModalProps {
  open: boolean;
  confirming: boolean;
  conflicts: ExportConflict[];
  onConfirm: () => void;
  onCancel: () => void;
}

/*
 * Shown when the backend refuses an export as conflicting (kontax-backend#926,
 * HTTP 409) rather than as a generic error: the local `computeWarning` hint on
 * the form is immediate feedback while picking dates, but the backend checked
 * the full audit history at request time, which is the only thing that can
 * catch a stale page or a second operator exporting concurrently. So this is
 * a decision, not a failure — confirming resends the same request with
 * `confirmConflicts=true`; cancelling exports nothing.
 */
export const ConflictModal = ({
  open,
  confirming,
  conflicts,
  onConfirm,
  onCancel,
}: ConflictModalProps) => {
  const { t } = useTranslation("ServiceTrackerExport");

  return (
    <Modal
      open={open}
      centered
      title={t("conflict.title")}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t("conflict.cancel")}
        </Button>,
        <Button
          key="confirm"
          type="primary"
          danger
          loading={confirming}
          onClick={onConfirm}
        >
          {t("conflict.confirm")}
        </Button>,
      ]}
    >
      {conflicts.map((conflict) => (
        <Alert
          key={`${conflict.kind}-${conflict.from}-${conflict.to}-${conflict.changedAt}`}
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={formatConflictMessage(conflict, t)}
        />
      ))}
    </Modal>
  );
};
